/**
 * The layer panel (spec 2026-08-22-02 §3).
 *
 * The scene is *per-asset layers*: every entry the frame resolves is one asset's
 * geometry. The panel therefore does two things at once — group-level
 * visibility / opacity for the five drawing layers, and, inside each group, the
 * list of contributing elements with their owning asset's name and type.
 * Selection is bidirectional: clicking a row selects on canvas, and selecting on
 * canvas scrolls the row into view. Shift-clicking a row builds a group exactly
 * as shift-clicking the drawing does (spec 2026-09-06-01 §1), and every member
 * of that group is highlighted here — the primary strongest.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiChevronDown, mdiChevronRight, mdiEyeOffOutline, mdiEyeOutline, mdiImageOutline, mdiTargetVariant } from '@mdi/js'
import { Button } from '../ui/button'
import type { LayerId, LayerStates } from './engine/render'
import {
    isGrouperElement,
    ownerName,
    ownerNameIsDerived,
    sceneLayers,
    spaceFallbackName,
    type EditorElement,
    type PlanNetwork,
    type RiserElement,
    type RunElement,
    type SceneDoc,
} from './engine/scene'
import { EmptyState } from '../ui/empty-state'
import { NetworkSwatch } from './NetworkControls'

interface LayerPanelProps {
    doc: SceneDoc
    /**
     * Bumped by the page on every engine event. The engine mutates its document
     * *in place*, so the element array's identity is not a usable memo key —
     * this counter is.
     */
    version: number
    layers: LayerStates
    onLayerChange: (id: LayerId, patch: Partial<{ on: boolean; opacity: number }>) => void
    onUnderlayChange: (key: string, patch: Partial<{ visible: boolean; opacity: number }>) => void
    selectedKey: string | null
    /**
     * Every selected element, primary included — the panel highlights the
     * whole group, not just the one the inspector's fields are anchored on
     * (spec 2026-09-06-01 §1).
     */
    selectedKeys?: string[]
    /**
     * `extend` is the row's Shift: add to / remove from the group rather than
     * replace it, and — like the canvas — do not pan while doing it.
     */
    onSelect: (key: string, opts?: { extend?: boolean }) => void
    /** Raster plans on the context asset that are not registered in the frame yet. */
    unregisteredUnderlays: { repUid: string; title: string }[]
    onUseAsUnderlay: (repUid: string) => void
    registeringUnderlay: boolean
    /** The registration selected for direct manipulation (spec §6). */
    selectedLayerUid: string | null
    onSelectLayer: (registrationUid: string | null) => void
    onCalibrateLayer: (registrationUid: string) => void
    /** Localized asset-type name — what an unnamed room is listed as. */
    typeLabelOf: (code: string) => string
    /** Network codes whose runs are hidden (per network, per family). */
    hiddenNetworks?: ReadonlySet<string>
    onToggleNetworks?: (codes: string[], hidden: boolean) => void
    /** Localized network label, from the ontology's catalogue. */
    networkLabelOf?: (code: string) => string
    /**
     * Files an underlay under a discipline (`null` for none), spec 2026-10-21-11.
     * Absent, the row shows no select.
     */
    onUnderlayDiscipline?: (repUid: string, discipline: string | null) => void
    /** `provenance.residual_mm` of each registration, when its transform was fitted. */
    residualByRegistration?: ReadonlyMap<string, number>
    /** Locale-aware date of a `YYYY-MM-DD` document date. */
    formatDocumentDate?: (date: string) => string
}

/** The plan layer disciplines, in the order the select lists them. */
export const PLAN_DISCIPLINES = ['architecture', 'fire_safety', 'hvac', 'power', 'low_voltage', 'plumbing'] as const

const GROUPS: { layer: LayerId; kinds: EditorElement['kind'][] }[] = [
    { layer: 'walls', kinds: ['wall'] },
    { layer: 'spaces', kinds: ['space'] },
    { layer: 'openings', kinds: ['opening'] },
    // Risers sit with the runs of their family: hiding plumbing hides both.
    { layer: 'runs', kinds: ['run', 'riser'] },
    { layer: 'equipment', kinds: ['equipment'] },
]

/** The family a code outside the catalogue is filed under. */
const OTHER_FAMILY = 'other'

/**
 * The runs on the plan, filed family → network in catalogue order, with the
 * codes the catalogue lacks last under "other". Only what is drawn is listed:
 * a toggle for a network with no run on this floor would switch nothing.
 */
type NetworkElement = RunElement | RiserElement

function runTree(elements: NetworkElement[], networks: PlanNetwork[]): { family: string; networks: { code: string; runs: NetworkElement[] }[] }[] {
    const byCode = new Map<string, NetworkElement[]>()
    for (const el of elements) byCode.set(el.network, [...(byCode.get(el.network) ?? []), el])
    const families: { family: string; networks: { code: string; runs: NetworkElement[] }[] }[] = []
    const familyOf = (family: string) => {
        let f = families.find((x) => x.family === family)
        if (!f) {
            f = { family, networks: [] }
            families.push(f)
        }
        return f
    }
    for (const n of networks) {
        const runs = byCode.get(n.code)
        if (!runs) continue
        familyOf(n.family || OTHER_FAMILY).networks.push({ code: n.code, runs })
        byCode.delete(n.code)
    }
    for (const [code, runs] of [...byCode.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        familyOf(OTHER_FAMILY).networks.push({ code, runs })
    }
    return families
}

function elementLabel(el: EditorElement, typeLabelOf: (code: string) => string): string {
    // A run reads as the network asset it is linked to; unlinked, it belongs
    // to the floor's bundle and the floor's name would say nothing, so its
    // local id stands in.
    if (el.kind === 'run' || el.kind === 'riser') {
        const linked = el.owner.assetUid && el.owner.assetUid !== el.source.repOwner?.assetUid
        return (linked && ownerName(el.owner)) || (el.kind === 'riser' ? el.label : undefined) || el.source.localId
    }
    // A room nobody named — pending, auto-named on the server, or just handed
    // back by clearing the field — is listed the way it is drawn, "Chambre 2"
    // (spec 2026-08-31-02 §2). It comes before the drawing's own label because
    // that label is where the deleted name survives: the row would otherwise
    // keep the very name the panel just cleared.
    const derived = el.kind === 'space' ? spaceFallbackName(el.owner, typeLabelOf) : ''
    if (derived && ownerNameIsDerived(el.owner)) return derived
    // Otherwise a space's own name comes first: inside a bundle every element
    // shares the representation's owning asset, and four rows called "Floor 1"
    // tell the reader nothing (spec 2026-08-22-03 §3).
    if (el.kind === 'space' && el.name) return el.name
    const own = ownerName(el.owner)
    if (own) return own
    if (derived) return derived
    return el.owner.assetName || el.source.localId
}

export function LayerPanel(props: LayerPanelProps) {
    const { t } = useTranslation('assets')
    const listRef = useRef<HTMLDivElement>(null)
    // A floor's networks run to hundreds of segments: each network's rows stay
    // folded until opened, or until one of its runs is selected.
    const [openNetworks, setOpenNetworks] = useState<ReadonlySet<string>>(() => new Set())
    const selectedNetwork = useMemo(() => {
        const el = props.doc.elements.find((e) => e.key === props.selectedKey)
        return el?.kind === 'run' || el?.kind === 'riser' ? el.network : null
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.doc, props.version, props.selectedKey])

    const byLayer = useMemo(() => {
        const map = new Map<LayerId, EditorElement[]>()
        for (const group of GROUPS) {
            map.set(group.layer, props.doc.elements.filter((el) => group.kinds.includes(el.kind)))
        }
        return map
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.doc, props.version])

    // Selecting on canvas scrolls the matching row into view.
    useEffect(() => {
        if (!props.selectedKey || !listRef.current) return
        const row = listRef.current.querySelector(`[data-element-key="${props.selectedKey}"]`)
        row?.scrollIntoView({ block: 'nearest' })
    }, [props.selectedKey])

    const opacityRow = (layer: LayerId) => (
        <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={props.layers[layer].opacity}
            onChange={(e) => props.onLayerChange(layer, { opacity: Number(e.target.value) })}
            aria-label={t('planEditor.layer_opacity', { layer: t(`planEditor.layers.${layer}`) })}
            className="h-1 w-16 accent-[var(--primary)]"
            data-testid={`plan-editor-layer-opacity-${layer}`}
        />
    )

    // Every stored registration is a placeable, calibratable layer (spec §1/§6).
    const registrations = useMemo(
        () => sceneLayers(props.doc),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.doc, props.version],
    )

    return (
        <div className="flex h-full flex-col overflow-hidden" data-testid="plan-editor-layer-panel">
            <div className="border-b border-border px-3 py-2">
                <h2 className="text-sm font-semibold text-foreground">{t('planEditor.layers_title')}</h2>
                <p className="text-xs text-muted-foreground">{t('planEditor.layers_hint')}</p>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto">
                {GROUPS.map(({ layer }) => {
                    const elements = byLayer.get(layer) ?? []
                    const state = props.layers[layer]
                    // The `spaces` layer mixes two natures of the same drawing
                    // primitive — lot/common-part outlines and the rooms they
                    // contain — which the layer count and visibility/opacity
                    // stay one control for, but the list splits into two
                    // labeled sub-groups so a lot outline no longer reads as
                    // just another room (spec 2026-09-05-02).
                    const units = layer === 'spaces' ? elements.filter(isGrouperElement) : []
                    const rooms = layer === 'spaces' ? elements.filter((el) => !isGrouperElement(el)) : elements

                    const renderRow = (el: EditorElement) => {
                        const primary = props.selectedKey === el.key
                        // A companion is selected too — it just is not the one
                        // the inspector's fields are anchored on, so it reads
                        // as a lighter version of the same state.
                        const companion = !primary && (props.selectedKeys?.includes(el.key) ?? false)
                        return (
                        <button
                            key={el.key}
                            type="button"
                            data-element-key={el.key}
                            data-testid={`plan-editor-layer-row-${el.owner.assetUid ?? el.key}`}
                            aria-pressed={primary || companion}
                            onClick={(e) => props.onSelect(el.key, { extend: e.shiftKey })}
                            className={
                                'flex w-full items-baseline gap-2 px-3 py-1 text-left text-xs hover:bg-muted ' +
                                (primary
                                    ? 'bg-muted font-medium text-foreground'
                                    : companion
                                        ? 'bg-muted/50 text-foreground'
                                        : 'text-muted-foreground')
                            }
                        >
                            <span className="truncate">{elementLabel(el, props.typeLabelOf)}</span>
                            <span className="ml-auto shrink-0 text-[10px] uppercase text-muted-foreground">
                                {el.kind === 'run' || el.kind === 'riser'
                                    ? (props.networkLabelOf?.(el.network) ?? el.network)
                                    : el.owner.assetTypeCode ?? el.owner.newAsset?.typeCode ?? el.kind}
                            </span>
                            {!el.owner.assetUid && (
                                <span className="shrink-0 text-[10px] text-primary">{t('planEditor.new_badge')}</span>
                            )}
                        </button>
                        )
                    }

                    return (
                        <div key={layer} className="border-b border-border/60">
                            <div className="flex items-center gap-2 px-3 py-1.5">
                                <button
                                    type="button"
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-label={t(state.on ? 'planEditor.hide_layer' : 'planEditor.show_layer', {
                                        layer: t(`planEditor.layers.${layer}`),
                                    })}
                                    aria-pressed={state.on}
                                    title={t(`planEditor.layers.${layer}`)}
                                    data-testid={`plan-editor-layer-toggle-${layer}`}
                                    onClick={() => props.onLayerChange(layer, { on: !state.on })}
                                >
                                    <AppIcon path={state.on ? mdiEyeOutline : mdiEyeOffOutline} size="sm" />
                                </button>
                                <span className="flex-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    {t(`planEditor.layers.${layer}`)} ({elements.length})
                                </span>
                                {opacityRow(layer)}
                            </div>
                            {state.on && layer === 'spaces' && units.length > 0 && (
                                <>
                                    <div
                                        className="flex items-center gap-2 px-3 py-1"
                                        data-testid="plan-editor-layer-group-units"
                                    >
                                        <span className="flex-1 pl-5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                            {t('planEditor.layer_groups.units')} ({units.length})
                                        </span>
                                    </div>
                                    {units.map((el) => renderRow(el))}
                                </>
                            )}
                            {state.on && layer === 'spaces' && rooms.length > 0 && (
                                <>
                                    <div
                                        className="flex items-center gap-2 px-3 py-1"
                                        data-testid="plan-editor-layer-group-rooms"
                                    >
                                        <span className="flex-1 pl-5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                            {t('planEditor.layer_groups.rooms')} ({rooms.length})
                                        </span>
                                    </div>
                                    {rooms.map((el) => renderRow(el))}
                                </>
                            )}
                            {state.on && layer === 'runs' && runTree(elements as NetworkElement[], props.doc.networks ?? []).map(({ family, networks }) => {
                                const codes = networks.map((n) => n.code)
                                const familyHidden = codes.every((c) => props.hiddenNetworks?.has(c))
                                const familyLabel = t(`planEditor.network_families.${family}`, { defaultValue: family })
                                return (
                                    <div key={family} data-testid={`plan-editor-network-family-${family}`}>
                                        <div className="flex items-center gap-2 px-3 py-1 pl-5">
                                            <button
                                                type="button"
                                                className="text-muted-foreground hover:text-foreground"
                                                aria-label={t(familyHidden ? 'planEditor.show_layer' : 'planEditor.hide_layer', { layer: familyLabel })}
                                                aria-pressed={!familyHidden}
                                                data-testid={`plan-editor-network-family-toggle-${family}`}
                                                onClick={() => props.onToggleNetworks?.(codes, !familyHidden)}
                                            >
                                                <AppIcon path={familyHidden ? mdiEyeOffOutline : mdiEyeOutline} size="xs" />
                                            </button>
                                            <span className="flex-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                                {familyLabel}
                                            </span>
                                        </div>
                                        {networks.map(({ code, runs }) => {
                                            const hidden = props.hiddenNetworks?.has(code) ?? false
                                            const network = props.doc.networks?.find((n) => n.code === code)
                                            const label = props.networkLabelOf?.(code) ?? code
                                            const open = openNetworks.has(code) || selectedNetwork === code
                                            return (
                                                <div key={code} data-testid={`plan-editor-network-${code}`}>
                                                    <div className="flex items-center gap-2 px-3 py-1 pl-8 text-xs">
                                                        <button
                                                            type="button"
                                                            className="text-muted-foreground hover:text-foreground"
                                                            aria-label={t(hidden ? 'planEditor.show_layer' : 'planEditor.hide_layer', { layer: label })}
                                                            aria-pressed={!hidden}
                                                            data-testid={`plan-editor-network-toggle-${code}`}
                                                            onClick={() => props.onToggleNetworks?.([code], !hidden)}
                                                        >
                                                            <AppIcon path={hidden ? mdiEyeOffOutline : mdiEyeOutline} size="xs" />
                                                        </button>
                                                        <NetworkSwatch color={network?.color} dash={network?.dash} />
                                                        <button
                                                            type="button"
                                                            className="flex min-w-0 flex-1 items-center gap-1 text-left hover:text-foreground"
                                                            aria-expanded={open}
                                                            data-testid={`plan-editor-network-expand-${code}`}
                                                            onClick={() => setOpenNetworks((prev) => {
                                                                const next = new Set(prev)
                                                                if (next.has(code)) next.delete(code)
                                                                else next.add(code)
                                                                return next
                                                            })}
                                                        >
                                                            <span className={'truncate ' + (hidden ? 'text-muted-foreground line-through' : 'text-foreground')}>{label}</span>
                                                            <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{runs.length}</span>
                                                            <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="xs" />
                                                        </button>
                                                    </div>
                                                    {!hidden && open && runs.map((el) => renderRow(el))}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                            })}
                            {state.on && layer !== 'spaces' && layer !== 'runs' && elements.map((el) => renderRow(el))}
                        </div>
                    )
                })}

                <div className="border-b border-border/60">
                    <div className="flex items-center gap-2 px-3 py-1.5">
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={t(props.layers.underlays.on ? 'planEditor.hide_layer' : 'planEditor.show_layer', {
                                layer: t('planEditor.layers.underlays'),
                            })}
                            aria-pressed={props.layers.underlays.on}
                            title={t('planEditor.layers.underlays')}
                            data-testid="plan-editor-layer-toggle-underlays"
                            onClick={() => props.onLayerChange('underlays', { on: !props.layers.underlays.on })}
                        >
                            <AppIcon path={props.layers.underlays.on ? mdiEyeOutline : mdiEyeOffOutline} size="sm" />
                        </button>
                        <span className="flex-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t('planEditor.layers.underlays')} ({props.doc.underlays.length})
                        </span>
                    </div>
                    {props.doc.underlays.map((u) => (
                        <div
                            key={u.key}
                            className="px-3 py-1 text-xs text-muted-foreground"
                            data-testid={`plan-editor-underlay-row-${u.repUid}`}
                        >
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-label={t(u.visible ? 'planEditor.hide_layer' : 'planEditor.show_layer', {
                                        layer: u.assetName ?? t('planEditor.layers.underlays'),
                                    })}
                                    aria-pressed={u.visible}
                                    title={u.assetName ?? ''}
                                    data-testid={`plan-editor-underlay-toggle-${u.repUid}`}
                                    onClick={() => props.onUnderlayChange(u.key, { visible: !u.visible })}
                                >
                                    <AppIcon path={u.visible ? mdiEyeOutline : mdiEyeOffOutline} size="xs" />
                                </button>
                                <span className="truncate">{u.assetName || t('planEditor.layers.underlays')}</span>
                                <input
                                    type="range"
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={u.opacity}
                                    onChange={(e) => props.onUnderlayChange(u.key, { opacity: Number(e.target.value) })}
                                    aria-label={t('planEditor.layer_opacity', { layer: u.assetName ?? '' })}
                                    className="ml-auto h-1 w-16 accent-[var(--primary)]"
                                    data-testid={`plan-editor-underlay-opacity-${u.repUid}`}
                                />
                            </div>
                            {props.onUnderlayDiscipline && !u.context && (
                                <div className="mt-0.5 flex items-center gap-2 pl-5">
                                    <select
                                        className="h-5 min-w-0 flex-1 rounded border border-border bg-background px-1 text-[11px] text-foreground"
                                        aria-label={t('planEditor.layers.discipline')}
                                        value={u.discipline ?? ''}
                                        data-testid={`plan-editor-underlay-discipline-${u.repUid}`}
                                        onChange={(e) => props.onUnderlayDiscipline?.(u.repUid, e.target.value || null)}
                                    >
                                        <option value="">{t('planEditor.layers.discipline_none')}</option>
                                        {PLAN_DISCIPLINES.map((code) => (
                                            <option key={code} value={code}>{t(`planEditor.disciplines.${code}`)}</option>
                                        ))}
                                    </select>
                                    <span
                                        className="shrink-0 text-[10px]"
                                        data-testid={`plan-editor-underlay-date-${u.repUid}`}
                                    >
                                        {u.documentDate
                                            ? (props.formatDocumentDate?.(u.documentDate) ?? u.documentDate)
                                            : t('planEditor.layers.undated')}
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                    {props.unregisteredUnderlays.map((rep) => (
                        <div key={rep.repUid} className="flex items-center gap-2 px-3 py-1.5">
                            <AppIcon tone="muted" path={mdiImageOutline} size="xs" />
                            <span className="truncate text-xs text-muted-foreground">{rep.title}</span>
                            <Button
                                variant="outline"
                                size="sm"
                                className="ml-auto h-6 px-2 text-[11px]"
                                disabled={props.registeringUnderlay}
                                data-testid={`plan-editor-use-underlay-${rep.repUid}`}
                                onClick={() => props.onUseAsUnderlay(rep.repUid)}
                            >
                                {t('planEditor.use_as_underlay')}
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="border-b border-border/60" data-testid="plan-editor-registrations">
                    <div className="flex items-center gap-2 px-3 py-1.5">
                        <span className="flex-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t('planEditor.registrations_title')} ({registrations.length})
                        </span>
                    </div>
                    {registrations.length === 0 && (
                        <EmptyState
                            variant="inline"
                            className="px-3 pb-1.5 pt-0"
                            description={t('planEditor.registrations_empty')}
                            data-testid="plan-editor-registrations-empty"
                        />
                    )}
                    {registrations.map((layer) => (
                        <div key={layer.registrationUid} className="flex items-center gap-1 px-3 py-1">
                            <button
                                type="button"
                                data-testid={`plan-editor-layer-select-${layer.registrationUid}`}
                                title={t('planEditor.select_layer_hint')}
                                aria-pressed={props.selectedLayerUid === layer.registrationUid}
                                className={
                                    'flex min-w-0 flex-1 items-baseline gap-2 text-left text-xs hover:text-foreground ' +
                                    (props.selectedLayerUid === layer.registrationUid
                                        ? 'font-medium text-foreground'
                                        : 'text-muted-foreground')
                                }
                                onClick={() => props.onSelectLayer(
                                    props.selectedLayerUid === layer.registrationUid ? null : layer.registrationUid,
                                )}
                            >
                                <span className="truncate">{layer.label}</span>
                                {props.residualByRegistration?.has(layer.registrationUid) && (
                                    <span
                                        className="shrink-0 text-[10px]"
                                        title={t('planEditor.layers.residual_hint')}
                                        data-testid={`plan-editor-layer-residual-${layer.registrationUid}`}
                                    >
                                        {t('planEditor.layers.residual', {
                                            mm: Math.round(props.residualByRegistration.get(layer.registrationUid) ?? 0),
                                        })}
                                    </span>
                                )}
                                <span className="ml-auto shrink-0 text-[10px] uppercase">
                                    {t(`planEditor.layer_kinds.${layer.kind}`)}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="shrink-0 text-muted-foreground hover:text-foreground"
                                aria-label={t('planEditor.calibrate_layer', { layer: layer.label })}
                                title={t('planEditor.calibrate_layer', { layer: layer.label })}
                                data-testid={`plan-editor-calibrate-layer-${layer.registrationUid}`}
                                onClick={() => props.onCalibrateLayer(layer.registrationUid)}
                            >
                                <AppIcon path={mdiTargetVariant} size="xs" />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-2 px-3 py-1.5">
                    <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={t(props.layers.labels.on ? 'planEditor.hide_layer' : 'planEditor.show_layer', {
                            layer: t('planEditor.layers.labels'),
                        })}
                        aria-pressed={props.layers.labels.on}
                        title={t('planEditor.layers.labels')}
                        data-testid="plan-editor-layer-toggle-labels"
                        onClick={() => props.onLayerChange('labels', { on: !props.layers.labels.on })}
                    >
                        <AppIcon path={props.layers.labels.on ? mdiEyeOutline : mdiEyeOffOutline} size="sm" />
                    </button>
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {t('planEditor.layers.labels')}
                    </span>
                </div>
            </div>
        </div>
    )
}
